<template lang="pug">
OrderActionModal(v-if="item" :show="show" max-width="min(100vw - 2rem, 28rem)" @close="onClose")
  template(slot="header")
    .transfer-modal__modal-title.uppercase {{ sourceTier }}
  .transfer-modal
    .transfer-modal__product-row
      .transfer-modal__product
        InventoryProduct(
          :reference="item.product"
          :size="item.size"
          :quantity="item.quantity"
          :brand="item.brand"
          :folder="item.folder"
          :subfolder="item.subfolder"
          :image="item.image"
        )
      .transfer-modal__stock-actions
        button.transfer-modal__action(type="button" :disabled="busy || !item.quantity" @click="onRemoveAll") Eliminar todos
        button.transfer-modal__action(type="button" :disabled="busy" @click="onRemoveOne") Eliminar 1
        button.transfer-modal__action(type="button" :disabled="busy" @click="onAddOne") Agregar 1

    .input-container.arrow
      select.select(v-model.number="quantity" autocomplete="ño" required)
        option(v-for="option in quantityOptions" :key="option" :value="option") {{ option }}
      label.label.show Cantidad

    .transfer-modal__section(v-if="visibleTransferTiers.length")
      .transfer-modal__title Transferir a:
      .transfer-modal__tiers
        button.transfer-modal__tier(
          v-for="tier in visibleTransferTiers"
          :key="`visible-${tier}`"
          type="button"
          :disabled="busy"
          @click="onTierClick(tier)"
        ) {{ tier }}

    .transfer-modal__section(v-if="nearbyTiers.length")
      .transfer-modal__title Tiers cercanos:
      .transfer-modal__tiers
        button.transfer-modal__tier(
          v-for="tier in nearbyTiers"
          :key="`nearby-${tier}`"
          type="button"
          :disabled="busy"
          @click="onTierClick(tier)"
        ) {{ tier }}

    .transfer-modal__section(v-if="sameProductTiers.length")
      .transfer-modal__title Mismo producto en:
      .transfer-modal__tiers
        button.transfer-modal__tier(
          v-for="tier in sameProductTiers"
          :key="`same-${tier}`"
          type="button"
          :disabled="busy"
          @click="onTierClick(tier)"
        ) {{ tier }}

  .transfer-modal__confirmation(v-if="showConfirmation" @click.self="closeConfirmation")
    .transfer-modal__confirmation-box(@click.stop="")
      h3.transfer-modal__confirmation-title Confirmación
      p.transfer-modal__confirmation-text {{ confirmationMessage }} ¿Deseas continuar?
      .transfer-modal__confirmation-actions
        button.transfer-modal__confirmation-btn(type="button" @click="closeConfirmation") No
        button.transfer-modal__confirmation-btn.transfer-modal__confirmation-btn--primary(type="button" @click="acceptConfirmation") Sí
</template>

<script>
import OrderActionModal from '@/components/pages/order/refund/OrderActionModal.vue'
import InventoryProduct from './Product.vue'
import modals from '@my/store/modals'

export default {
  name: 'InventoryTransferModal',

  components: {
    OrderActionModal,
    InventoryProduct
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    sourceTier: {
      type: String,
      default: ''
    },
    item: {
      type: Object,
      default: null
    },
    visibleTiers: {
      type: Array,
      default: () => []
    },
    tierGroups: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      quantity: null,
      quantityAtMax: true,
      busy: false,
      showConfirmation: false,
      confirmationMessage: '',
      pendingAction: null
    }
  },

  watch: {
    item: {
      immediate: true,
      handler (value, oldValue) {
        if (!value) {
          this.quantity = null
          this.quantityAtMax = true
          return
        }

        const max = value.quantity
        const sameItem = oldValue
          && oldValue.product === value.product
          && oldValue.size === value.size

        if (!sameItem) {
          this.quantity = max
          this.quantityAtMax = true
          return
        }

        const previousMax = oldValue.quantity

        if (max > previousMax && this.quantityAtMax) {
          this.quantity = max
        } else if (this.quantity > max) {
          this.quantity = max
        }

        this.quantityAtMax = this.quantity === max
      }
    },

    quantity (value) {
      if (!this.item || value == null) return
      this.quantityAtMax = value === this.item.quantity
    }
  },

  computed: {
    quantityOptions () {
      if (!this.item || !this.item.quantity) return [1]

      return Array.from({ length: this.item.quantity }, (_, index) => index + 1)
    },

    visibleTransferTiers () {
      return this.sortTiers(this.visibleTiers.filter(tier => tier !== this.sourceTier))
    },

    nearbyTiers () {
      const parsed = this.parseTierCode(this.sourceTier)
      if (!parsed) return []

      return [-3, -2, -1, 1, 2, 3]
        .map(offset => parsed.number + offset)
        .filter(number => number > 0)
        .map(number => `${parsed.letter}${number}`)
    },

    sameProductTiers () {
      if (!this.item) return []

      const product = this.item.product.toLowerCase()
      const size = this.item.size.toLowerCase()

      const tiers = this.tierGroups
        .filter(group => {
          if (group.tier === this.sourceTier) return false

          return group.items.some(entry => {
            if (entry.product.toLowerCase() !== product) return false
            return entry.size.toLowerCase() === size
          })
        })
        .map(group => group.tier)

      return this.sortTiers(tiers)
    }
  },

  methods: {
    parseTierCode (tier) {
      const trimmed = (tier || '').trim()
      if (!trimmed) return null

      const letter = trimmed.charAt(0).toLowerCase()
      const numberPart = trimmed.slice(1)

      if (!/^\d+$/.test(numberPart)) return null

      return { letter, number: parseInt(numberPart, 10) }
    },

    compareTiers (tierA, tierB) {
      const parsedA = this.parseTierCode(tierA)
      const parsedB = this.parseTierCode(tierB)

      if (!parsedA || !parsedB) return String(tierA).localeCompare(String(tierB))

      const letterCompare = parsedA.letter.localeCompare(parsedB.letter)
      if (letterCompare !== 0) return letterCompare

      return parsedA.number - parsedB.number
    },

    sortTiers (tiers) {
      return [...tiers].sort((tierA, tierB) => this.compareTiers(tierA, tierB))
    },

    onClose () {
      this.closeConfirmation()
      this.$emit('close')
    },

    requestConfirmation (message, action) {
      this.confirmationMessage = message
      this.pendingAction = action
      this.showConfirmation = true
    },

    closeConfirmation () {
      this.showConfirmation = false
      this.confirmationMessage = ''
      this.pendingAction = null
    },

    acceptConfirmation () {
      const action = this.pendingAction
      this.closeConfirmation()
      if (action) action()
    },

    async runStockAction (action) {
      if (this.busy || !this.item) return

      this.busy = true

      try {
        const result = await action()

        if (result) {
          this.$emit('updated', typeof result === 'object' ? result : {})
        }
      } finally {
        this.busy = false
      }
    },

    onRemoveOne () {
      this.requestConfirmation(
        `Se eliminará 1 unidad de ${this.item.product} (talla ${this.item.size}) del nivel ${this.sourceTier}.`,
        () => this.runStockAction(async () => {
          const success = await this.$store.dispatch('deleteFromTier', {
            reference: this.item.product,
            size: this.item.size,
            tier: this.sourceTier,
            quantity: 1
          })

          if (success) {
            modals.showMessage({
              message: `Se eliminó 1 unidad de ${this.item.product} del nivel ${this.sourceTier}`
            })
          }

          return success
        })
      )
    },

    onRemoveAll () {
      const quantity = this.item.quantity

      this.requestConfirmation(
        `Se eliminarán ${quantity} unidad(es) de ${this.item.product} (talla ${this.item.size}) del nivel ${this.sourceTier}.`,
        () => this.runStockAction(async () => {
          const success = await this.$store.dispatch('deleteFromTier', {
            reference: this.item.product,
            size: this.item.size,
            tier: this.sourceTier,
            quantity
          })

          if (success) {
            modals.showMessage({
              message: `Se eliminaron ${quantity} unidad(es) de ${this.item.product} del nivel ${this.sourceTier}`
            })
          }

          return success
        })
      )
    },

    onAddOne () {
      this.requestConfirmation(
        `Se agregará 1 unidad de ${this.item.product} (talla ${this.item.size}) al nivel ${this.sourceTier}.`,
        () => this.runStockAction(async () => {
          const success = await this.$store.dispatch('addToTier', {
            reference: this.item.product,
            size: this.item.size,
            tier: this.sourceTier,
            itemTemplate: this.item
          })

          if (success) {
            modals.showMessage({
              message: `Se agregó 1 unidad de ${this.item.product} al nivel ${this.sourceTier}`
            })
          }

          return success
        })
      )
    },

    onTierClick (targetTier) {
      this.requestConfirmation(
        `Se transferirán ${this.quantity} unidad(es) de ${this.item.product} (talla ${this.item.size}) de ${this.sourceTier} a ${targetTier}.`,
        () => this.runStockAction(async () => {
          const success = await this.$store.dispatch('transferBetweenTiers', {
            reference: this.item.product,
            size: this.item.size,
            sourceTier: this.sourceTier,
            targetTier,
            quantity: this.quantity,
            itemTemplate: this.item
          })

          if (success) {
            modals.showMessage({
              message: `Se transfirieron ${this.quantity} unidad(es) de ${this.item.product} de ${this.sourceTier} a ${targetTier}`
            })
          }

          return success ? { targetTier } : false
        })
      )
    }
  }
}
</script>

<style lang="stylus" scoped>
.transfer-modal
  padding 0 1.5rem 1.5rem
  overflow-y auto

.transfer-modal__modal-title
  font-size 1.5em
  font-weight 700

.transfer-modal__product-row
  display flex
  align-items flex-end
  gap 1rem
  margin-bottom 1.25rem

.transfer-modal__product
  flex 0 0 12rem
  max-width 12rem

.transfer-modal__stock-actions
  display flex
  flex-direction column
  gap .65em

.transfer-modal__action
  font-family inherit
  font-size 1em
  font-weight 600
  text-transform uppercase
  padding .45em .75em
  background-color #e9e9e7
  border none
  cursor pointer
  white-space nowrap

  &:hover
    opacity .75

  &:disabled
    opacity .35
    cursor not-allowed

.transfer-modal__section
  margin-top 1.25rem

.transfer-modal__title
  font-size 1rem
  font-weight 600
  text-transform uppercase
  margin-bottom .65rem

.transfer-modal__tiers
  display flex
  flex-wrap wrap
  gap .5em

.transfer-modal__tier
  font-family inherit
  font-size 1em
  font-weight 600
  text-transform uppercase
  padding .35em .65em
  background-color #e9e9e7
  border none
  cursor pointer

  &:hover
    opacity .75

  &:disabled
    opacity .35
    cursor not-allowed

.transfer-modal__confirmation
  position fixed
  inset 0
  z-index 12100
  display flex
  justify-content center
  align-items center
  background-color rgba(0, 0, 0, .45)
  padding 1rem

.transfer-modal__confirmation-box
  background-color var(--background-principal, #fff)
  width 100%
  max-width 28rem
  padding 1.5rem 2rem

.transfer-modal__confirmation-title
  font-size 1.2rem
  font-weight 600
  text-transform uppercase
  margin 0 0 .75rem

.transfer-modal__confirmation-text
  font-size 1.05rem
  margin 0 0 1.25rem
  line-height 1.4

.transfer-modal__confirmation-actions
  display flex
  justify-content center
  gap 1rem

.transfer-modal__confirmation-btn
  font-family inherit
  font-size 1rem
  font-weight 600
  text-transform uppercase
  padding .55em 1.25em
  background-color #e9e9e7
  border none
  cursor pointer

  &:hover
    opacity .75

.transfer-modal__confirmation-btn--primary
  background-color #000
  color #fff
</style>



// WEBPACK FOOTER //
// src/components/pages/inventory/edit/TransferModal.vue